import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Auto-reload on ChunkLoadError (stale deploy) — once per session path
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      const reloadedKey = 'chunk_reload_' + window.location.pathname;
      if (!sessionStorage.getItem(reloadedKey)) {
        sessionStorage.setItem(reloadedKey, '1');
        window.location.reload();
        return;
      }
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message?.includes('Loading chunk');

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom color="error">
              {isChunkError ? 'Actualización disponible' : 'Something went wrong'}
            </Typography>
            <Typography variant="body1" paragraph>
              {isChunkError
                ? 'Hay una nueva versión de la aplicación. Por favor recarga la página.'
                : 'An error occurred while rendering the application.'}
            </Typography>
            {!isChunkError && this.state.error && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="error">
                  Error: {this.state.error.toString()}
                </Typography>
                {this.state.errorInfo && (
                  <Box
                    component="pre"
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.75rem',
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              {isChunkError ? (
                <Button variant="contained" onClick={this.handleReload}>
                  Recargar página
                </Button>
              ) : (
                <>
                  <Button variant="contained" onClick={this.handleReset}>
                    Try Again
                  </Button>
                  <Button variant="outlined" onClick={this.handleReload}>
                    Reload Page
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
